import { http } from '@/api/http'
import type { Reservation } from '@/lib/pos-types'
import { normalizeReservation, unwrapList, unwrapSingle } from '@/api/normalizers'

type ListParams = {
  from?: string
  to?: string
  limit?: number
  offset?: number
  scope?: 'upcoming' | 'history'
  status?: Reservation['status'] | Reservation['status'][]
}

export const reservationsApi = {
  list: async (params?: ListParams): Promise<Reservation[]> => {
    const res = await http.get<unknown>('/reservations', { params })
    return unwrapList(res.data).map(normalizeReservation)
  },
  create: async (payload: {
    tableId?: string | null
    reserverName: string
    reserverPhone?: string
    reserverEmail?: string
    partySize: number
    notes?: string
    reservedAt: string
    preBlockMinutes: number
    unlockCode: string
    kitchenLeadMinutes: number
    sendMode?: Reservation['sendMode']
    printTicketTiming?: Reservation['printTicketTiming']
    items?: Array<{
      id?: string
      product_id: string
      product_name?: string
      unit_price?: number
      quantity: number
      notes?: string
    }>
  }): Promise<Reservation> => {
    const res = await http.post<unknown>('/reservations', {
      table_id: payload.tableId ?? null,
      reserver_name: payload.reserverName,
      reserver_phone: payload.reserverPhone,
      reserver_email: payload.reserverEmail,
      party_size: payload.partySize,
      notes: payload.notes,
      reserved_at: payload.reservedAt,
      pre_block_minutes: payload.preBlockMinutes,
      unlock_code: payload.unlockCode,
      kitchen_lead_minutes: payload.kitchenLeadMinutes,
      send_mode: payload.sendMode,
      print_ticket_timing: payload.printTicketTiming,
      items: payload.items,
    })
    return normalizeReservation(unwrapSingle(res.data))
  },
  update: async (
    id: string,
    payload: Partial<{
      tableId: string | null
      reserverName: string
      reserverPhone?: string | null
      reserverEmail?: string | null
      partySize: number
      notes?: string | null
      reservedAt: string
      preBlockMinutes: number
      kitchenLeadMinutes: number
      sendMode: Reservation['sendMode']
      printTicketTiming: Reservation['printTicketTiming']
      status: Reservation['status']
      items: Array<{
        id?: string
        product_id: string
        product_name?: string
        unit_price?: number
        quantity: number
        notes?: string
      }>
    }>
  ): Promise<Reservation> => {
    const res = await http.patch<unknown>(`/reservations/${encodeURIComponent(id)}`, {
      ...(payload.tableId !== undefined ? { table_id: payload.tableId } : {}),
      ...(payload.reserverName !== undefined ? { reserver_name: payload.reserverName } : {}),
      ...(payload.reserverPhone !== undefined ? { reserver_phone: payload.reserverPhone } : {}),
      ...(payload.reserverEmail !== undefined ? { reserver_email: payload.reserverEmail } : {}),
      ...(payload.partySize !== undefined ? { party_size: payload.partySize } : {}),
      ...(payload.notes !== undefined ? { notes: payload.notes } : {}),
      ...(payload.reservedAt !== undefined ? { reserved_at: payload.reservedAt } : {}),
      ...(payload.preBlockMinutes !== undefined ? { pre_block_minutes: payload.preBlockMinutes } : {}),
      ...(payload.kitchenLeadMinutes !== undefined ? { kitchen_lead_minutes: payload.kitchenLeadMinutes } : {}),
      ...(payload.sendMode !== undefined ? { send_mode: payload.sendMode } : {}),
      ...(payload.printTicketTiming !== undefined ? { print_ticket_timing: payload.printTicketTiming } : {}),
      ...(payload.status !== undefined ? { status: payload.status } : {}),
      ...(payload.items !== undefined ? { items: payload.items } : {}),
    })
    return normalizeReservation(unwrapSingle(res.data))
  },
}
